import { type ComponentType, useEffect, useRef } from 'react';

import { observer } from 'mobx-react';

import type { WizardChildProps } from '../../../../components/Wizard/types';
import { SkipToken, useCertainCleanup } from '../../../../hooks/useCertainCleanup';
import type { PocState, PocStep } from '../../../../stores/poc/types';
import type { RootStore } from '../../../../stores/RootStore';
import { useStores } from '../../../../stores/useStores';

import { PlaceholderView } from './PlaceholderView';

export function withCurrentSessionState<S extends PocStep, A>(
  Component: ComponentType<WizardChildProps & { pocState: PocState<S> }>,
  requiredStep: S,
  cleanup?: {
    getArgs: (pocState: PocState) => A | typeof SkipToken;
    cleanupFn: (arg: A, stores: RootStore) => unknown;
  },
) {
  return observer(function WrappedPocWizardView(props: WizardChildProps) {
    const stores = useStores();
    const { pocStore, payRedeemStore } = stores;
    const isCurrentRef = useRef(props.isCurrent);

    useEffect(() => {
      isCurrentRef.current = props.isCurrent;
    }, [props.isCurrent]);

    useCertainCleanup(
      (arg) => {
        if (cleanup) {
          cleanup.cleanupFn(arg, stores);
        }
      },
      () => {
        if (!cleanup || !isCurrentRef.current || !pocStore.pocState) {
          return SkipToken;
        }

        return cleanup.getArgs(pocStore.pocState);
      },
    );

    const shouldShowPlaceholder =
      !props.isCurrent || pocStore.pocState == null || pocStore.pocState.step !== requiredStep;

    if (shouldShowPlaceholder) {
      return <PlaceholderView isCashRedeem={payRedeemStore.withdrawBalanceType === 'CASH'} />;
    }

    return <Component {...props} pocState={pocStore.pocState as never} />;
  });
}
